import { useEffect, useMemo, useState } from "react";
import type { AsaSyncStatus } from "@/lib/types";

/**
 * Sits above the Overview content while the ASA collector is pulling
 * the org's 7-day initial data set, and one more time after it
 * finishes to confirm success. Two variants, picked off the same
 * `sync_status` payload:
 *
 *   1. `initial_sync_in_progress` → "collecting…" with a per-day
 *      checklist (today ✓ / yesterday ✓ / T-2 ⏳ …) so the admin
 *      knows which days have landed without reading tea leaves.
 *
 *   2. `initial_sync_completed_at` set AND not-dismissed-yet →
 *      green "Sync complete" variant with a close button. Kept
 *      around across sessions so a user who kicked the sync and
 *      walked away still sees the confirmation when they return.
 *      Disappears after the user clicks X or after we've shown it
 *      in two page loads — whichever comes first.
 *
 * `useOverview` already polls every 15s while sync is in progress
 * (sync_status.initial_sync_in_progress is TRUE), so the checklist
 * updates itself without a manual refresh.
 */
export function AsaInitialSyncBanner({ status }: { status: AsaSyncStatus }) {
  if (status.initial_sync_in_progress) {
    return <ProgressBanner status={status} />;
  }
  if (status.initial_sync_completed_at) {
    return <DoneBanner completedAt={status.initial_sync_completed_at} />;
  }
  return null;
}

// ---------------------------------------------------------------------------
// In-progress variant
// ---------------------------------------------------------------------------

function ProgressBanner({ status }: { status: AsaSyncStatus }) {
  const expected = useMemo(() => buildExpectedWindow(7), []);
  const syncedSet = useMemo(
    () => new Set(status.synced_dates),
    [status.synced_dates]
  );

  return (
    <div
      className="card"
      style={{
        padding: "12px 16px",
        marginBottom: 16,
        background: "var(--c-info-50, #e8f1ff)",
        borderColor: "transparent",
        display: "flex",
        alignItems: "flex-start",
        gap: 14,
      }}
    >
      <Spinner />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: 13.5,
            color: "var(--c-info-600, #1e56c9)",
          }}
        >
          Collecting data from Apple Search Ads…
        </div>
        <div
          style={{
            fontSize: 12.5,
            color: "var(--c-text-2)",
            marginTop: 4,
            display: "flex",
            gap: 14,
            flexWrap: "wrap",
          }}
        >
          {expected.map((d) => {
            const synced = syncedSet.has(d.iso);
            return (
              <span
                key={d.iso}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  color: synced
                    ? "var(--c-pos-600, #1b7a41)"
                    : "var(--c-text-3)",
                }}
                title={d.iso}
              >
                {synced ? "✓" : "○"} {d.label}
              </span>
            );
          })}
        </div>
        <div
          className="muted"
          style={{ fontSize: 11.5, marginTop: 6 }}
        >
          Usually 5–40 min depending on the size of your account.
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Completed variant — sticky until dismissed or shown twice
// ---------------------------------------------------------------------------

const DISMISS_THRESHOLD = 2; // show on at most 2 distinct page loads

function DoneBanner({ completedAt }: { completedAt: string }) {
  // localStorage state is keyed by the specific completion timestamp,
  // so a fresh sync (new completedAt) gets its own counter and the
  // banner reappears — we're not hiding forever after one sync.
  const storageKey = `oneads.sync_done_banner:${completedAt}`;
  const [hidden, setHidden] = useState<boolean>(() => readDismissed(storageKey));

  // Increment the "shown" counter once per mount (= page load). When
  // it reaches the threshold, hide on the next mount.
  useEffect(() => {
    if (hidden) return;
    const prev = Number(window.localStorage.getItem(`${storageKey}:count`) ?? "0");
    const next = prev + 1;
    window.localStorage.setItem(`${storageKey}:count`, String(next));
    if (next >= DISMISS_THRESHOLD) {
      window.localStorage.setItem(`${storageKey}:dismissed`, "1");
    }
  }, [storageKey, hidden]);

  if (hidden) return null;

  return (
    <div
      className="card"
      style={{
        padding: "12px 16px",
        marginBottom: 16,
        background: "var(--c-pos-50, #e7f8ee)",
        borderColor: "transparent",
        display: "flex",
        alignItems: "center",
        gap: 14,
      }}
    >
      <span
        aria-hidden
        style={{
          display: "inline-grid",
          placeItems: "center",
          width: 22,
          height: 22,
          borderRadius: "50%",
          background: "var(--c-pos-500, #2fb46f)",
          color: "#fff",
          fontSize: 13,
          fontWeight: 700,
          flex: "none",
        }}
      >
        ✓
      </span>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: 13.5,
            color: "var(--c-pos-600, #1b7a41)",
          }}
        >
          Data is synchronized with your Apple Search Ads account.
        </div>
        <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
          All 7 days pulled. The collector keeps the numbers up to
          date automatically from here on.
        </div>
      </div>
      <button
        onClick={() => {
          window.localStorage.setItem(`${storageKey}:dismissed`, "1");
          setHidden(true);
        }}
        aria-label="Dismiss"
        style={{
          border: 0,
          background: "transparent",
          color: "var(--c-text-3)",
          cursor: "pointer",
          fontSize: 18,
          lineHeight: 1,
          padding: 4,
        }}
      >
        ×
      </button>
    </div>
  );
}

function readDismissed(storageKey: string): boolean {
  return window.localStorage.getItem(`${storageKey}:dismissed`) === "1";
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function Spinner() {
  return (
    <span
      aria-hidden
      style={{
        display: "inline-block",
        width: 16,
        height: 16,
        borderRadius: "50%",
        border: "2px solid var(--c-info-200, #b7d1ff)",
        borderTopColor: "var(--c-info-600, #1e56c9)",
        animation: "oneads-sync-spin 0.9s linear infinite",
        flex: "none",
        marginTop: 2,
      }}
    >
      <style>{`@keyframes oneads-sync-spin { to { transform: rotate(360deg); } }`}</style>
    </span>
  );
}

type ExpectedDay = { iso: string; label: string };

function buildExpectedWindow(days: number): ExpectedDay[] {
  const out: ExpectedDay[] = [];
  const now = new Date();
  // Format the synthetic UTC-midnight Date in UTC so the displayed
  // month/day matches the ISO we're sending — otherwise someone in
  // UTC+3 would see "Apr 21" next to an iso of "2026-04-22" the
  // moment their local clock tips into the next day.
  const shortDate = new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
  const weekDay = new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    timeZone: "UTC",
  });
  for (let i = 0; i < days; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - i));
    const iso = d.toISOString().slice(0, 10);
    const dateStr = shortDate.format(d); // "Apr 22"
    let label: string;
    if (i === 0) label = `Today · ${dateStr}`;
    else if (i === 1) label = `Yesterday · ${dateStr}`;
    else label = `${weekDay.format(d)} · ${dateStr}`; // "Sat · Apr 19"
    out.push({ iso, label });
  }
  return out;
}
